import React, { useEffect, useState } from "react";
import axios from "axios";
import "./LongtermTrackContainer.css";

function LongtermTrackContainer() {
  const [records, setRecords] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [propData, setPropData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cycleData, setCycleData] = useState([]);
  const [showCyclePopup, setShowCyclePopup] = useState(false);
  const [savingCycle, setSavingCycle] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [expandedBucket, setExpandedBucket] = useState(null); // 🔽 toggle state
  const [nearestAlert, setNearestAlert] = useState(null);
  const [blinker, setBlinker] = useState(false);
  const [blinkColor, setBlinkColor] = useState("red"); // "red" or "green"

  const token = localStorage.getItem("authToken");

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        "  https://quatdashboard.com:8084/api/tradebucket/grouped",
        axiosConfig,
      );
      setRecords(res.data);
      evaluateNearest(res.data);
    } catch (err) {
      console.error("❌ Error fetching tradebucket data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCycleConfig = async () => {
    try {
      const res = await axios.get(
        "  https://quatdashboard.com:8084/api/cycles/current",
        axiosConfig,
      );
      setCycleData(res.data);
      setShowCyclePopup(true);
    } catch (err) {
      console.error("❌ Error fetching cycle data:", err);
      alert("Failed to fetch cycle configuration!");
    }
  };

  const saveCycleChanges = async () => {
    setSavingCycle(true);
    try {
      for (const cycle of cycleData) {
        await axios.put(
          `  https://quatdashboard.com:8084/api/cycles/update/${cycle.cycle}`,
          cycle,
          axiosConfig,
        );
      }
      alert("✅ Cycle percentages updated successfully!");
      setShowCyclePopup(false);
    } catch (err) {
      console.error("❌ Error updating cycles:", err);
      alert("Failed to update cycle percentages!");
    } finally {
      setSavingCycle(false);
    }
  };

  const fetchPropData = async () => {
    try {
      const res = await axios.get(
        "  https://quatdashboard.com:8084/api/longtermprop/first",
        axiosConfig,
      );
      setPropData(res.data);
      setShowPopup(true);
    } catch (err) {
      console.error("❌ Error fetching longtermprop data:", err);
      alert("Failed to fetch configuration data!");
    }
  };
  const evaluateNearest = (data) => {
    let nearest = null;
    let minDiff = Infinity;
    let color = "red"; // default

    Object.entries(data).forEach(([bucket, obj]) => {
      (obj.tradeBuckets || [])
        .filter((r) => r.status === "ACTIVE")
        .forEach((r) => {
          const nextAvgDiff = Math.abs((r.nextAvg || 0) - r.ltp);
          const slDiff = Math.abs((obj.slPrice || 0) - r.ltp);

          const currentNearest = Math.min(nextAvgDiff, slDiff);

          if (currentNearest < minDiff) {
            minDiff = currentNearest;

            // select GREEN if nextAvg is closer, RED if SL is closer
            color = nextAvgDiff < slDiff ? "green" : "red";

            nearest = {
              bucket,
              symbol: r.symbol,
              ltp: r.ltp,
              nextAvg: r.nextAvg,
              slPrice: obj.slPrice,
              diffValue: currentNearest,
              reason: nextAvgDiff < slDiff ? "NEXT_AVG" : "SL",
            };
          }
        });
    });

    setBlinkColor(color);
    setNearestAlert(nearest);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        "  https://quatdashboard.com:8084/api/longtermprop/update",
        propData,
        axiosConfig,
      );
      alert("✅ Configuration updated successfully!");
      setEditMode(false);
    } catch (err) {
      console.error("❌ Error updating prop:", err);
      alert("Failed to update configuration!");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (nearestAlert) {
      const interval = setInterval(() => {
        setBlinker((b) => !b);
      }, 600);

      return () => clearInterval(interval);
    }
  }, [nearestAlert]);

  // Flattened tradeBuckets for filtering/search
  const allRecords = Object.entries(records).flatMap(([bucket, obj]) =>
    (obj.tradeBuckets || [])
      .filter(
        (r) =>
          r.status?.toLowerCase() === statusFilter.toLowerCase() &&
          [r.symbol, r.status, bucket]
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      )
      .map((item) => ({ ...item, bucket: parseInt(bucket) })),
  );

  return (
    <div className="lt-screen-container">
      <div className="lt-header">
        <h2>📊 Trade Bucket Overview</h2>
        <div className="lt-header-actions">
          <input
            type="text"
            placeholder="🔍 Search symbol, bucket, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lt-search"
          />
          {nearestAlert && (
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  opacity: blinker ? 1 : 0,
                  transition: "opacity 120ms linear",
                  background: "none",
                  border: "none",
                  padding: 0,
                  color:
                    nearestAlert.reason === "NEXT_AVG" ? "#006600" : "#b30000",
                }}
              >
                {nearestAlert.reason === "NEXT_AVG" ? "🟢" : "🔴"} ALERT:{" "}
                {nearestAlert.symbol} (Bucket {nearestAlert.bucket}) — LTP{" "}
                {nearestAlert.ltp.toFixed(2)} is near{" "}
                {nearestAlert.reason === "NEXT_AVG"
                  ? `NEXT AVG: ${nearestAlert.nextAvg.toFixed(2)}`
                  : `SL PRICE: ${nearestAlert.slPrice.toFixed(2)}`}
              </span>
            </div>
          )}

          <div className="lt-filter-btns">
            <button
              className={`lt-btn ${
                statusFilter === "ACTIVE" ? "selected" : ""
              }`}
              onClick={() => setStatusFilter("ACTIVE")}
            >
              ✅ Active
            </button>
            <button
              className={`lt-btn ${
                statusFilter === "STOPPED" ? "selected" : ""
              }`}
              onClick={() => setStatusFilter("STOPPED")}
            >
              ⛔ Stopped
            </button>
            <button className="lt-btn secondary" onClick={fetchData}>
              🔄 Refresh
            </button>
            <button className="lt-btn secondary" onClick={fetchPropData}>
              ⚙️ Config
            </button>
            <button className="lt-btn secondary" onClick={fetchCycleConfig}>
              🔁 Cycle Config
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="lt-loader">Loading trade buckets...</div>
      ) : allRecords.length === 0 ? (
        <div className="lt-loader">
          No {statusFilter.toLowerCase()} trade buckets found.
        </div>
      ) : (
        Object.entries(records).map(([bucket, obj]) => {
          const items = obj.tradeBuckets || [];
          const filteredItems = items.filter(
            (r) =>
              r.status?.toLowerCase() === statusFilter.toLowerCase() &&
              [r.symbol, r.status, bucket]
                .join(" ")
                .toLowerCase()
                .includes(searchTerm.toLowerCase()),
          );
          if (filteredItems.length === 0) return null;

          return (
            <div key={bucket} className="lt-bucket-card">
              <div
                className="lt-bucket-header"
                onClick={() =>
                  setExpandedBucket(expandedBucket === bucket ? null : bucket)
                }
              >
                <h3>
                  🪣 Bucket {bucket}
                  {filteredItems.length > 1 ? "s" : ""}
                </h3>
                <button className="lt-btn small">
                  {expandedBucket === bucket ? "🔼 Hide" : "🔽 Show"}
                </button>
              </div>

              {/* --- TradeBucket Summary Table --- */}
              <table className="lt-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Symbol</th>
                    <th>Entry Price</th>
                    <th>Avg Price</th>
                    <th>LTP</th>
                    <th>Qty</th>
                    <th>Cycle Count</th>
                    <th>Next Avg</th>
                    <th>SL Price</th>
                    <th>PNL (₹)</th>
                    <th>Entry Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((r) => {
                    const pnl =
                      r.status === "ACTIVE" ? (r.ltp - r.avgprice) * r.qty : 0;
                    const slPrice = obj.slPrice || 0;

                    return (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td className="symbol">{r.symbol}</td>
                        <td>{r.entryprice?.toFixed(2)}</td>
                        <td>{r.avgprice?.toFixed(2)}</td>
                        <td>{r.ltp ? r.ltp.toFixed(2) : "-"}</td>
                        <td>{r.qty}</td>
                        <td>{r.cyclecount}</td>
                        <td>{r.nextAvg?.toFixed(2)}</td>
                        <td>{slPrice.toFixed(2)}</td>
                        <td
                          className={
                            pnl > 0 ? "positive" : pnl < 0 ? "light-red" : ""
                          }
                        >
                          {pnl.toFixed(2)}
                        </td>
                        <td>{new Date(r.entrydate).toLocaleString()}</td>
                        <td
                          className={
                            r.status === "ACTIVE" ? "positive" : "light-red"
                          }
                        >
                          {r.status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* --- Dropdown Individual Trades --- */}
              {expandedBucket === bucket && obj.individualTrades && (
                <div className="lt-individual-trades">
                  <h4>📄 Individual Trades</h4>
                  <table className="lt-table small">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Symbol</th>
                        <th>Entry Price</th>
                        <th>Qty</th>
                        <th>Cycle</th>
                        <th>Entry Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {obj.individualTrades.map((t) => (
                        <tr key={t.id}>
                          <td>{t.id}</td>
                          <td>{t.symbol}</td>
                          <td>{t.entryprice.toFixed(2)}</td>
                          <td>{t.qty}</td>
                          <td>{t.cyclecount}</td>
                          <td>{new Date(t.entrydate).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}

      <p className="lt-footer">Showing {allRecords.length} record(s)</p>

      {/* ⚙️ Config Popup */}
      {showPopup && propData && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <h3>⚙️ Longterm Configuration</h3>
            <div className="popup-fields">
              {["basecap", "avgpercentage", "slpercentage", "cyclecount"].map(
                (field) => (
                  <div key={field} className="popup-field">
                    <label>
                      {field === "basecap"
                        ? "Base Capital (₹)"
                        : field === "avgpercentage"
                          ? "Average %"
                          : field === "slpercentage"
                            ? "Stoploss %"
                            : "Cycle Count"}
                    </label>
                    <input
                      type="number"
                      disabled={!editMode}
                      value={propData[field]}
                      onChange={(e) =>
                        setPropData({ ...propData, [field]: e.target.value })
                      }
                    />
                  </div>
                ),
              )}
            </div>
            <div className="popup-actions">
              {!editMode ? (
                <button className="lt-btn" onClick={() => setEditMode(true)}>
                  ✏️ Edit
                </button>
              ) : (
                <button
                  className="lt-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  💾 {saving ? "Saving..." : "Save"}
                </button>
              )}
              <button
                className="lt-btn secondary"
                onClick={() => setShowPopup(false)}
              >
                ✖ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔁 Cycle Config Popup */}
      {showCyclePopup && (
        <div className="popup-overlay" onClick={() => setShowCyclePopup(false)}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <h3>🔁 Cycle Configuration</h3>
            <table className="popup-table">
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>Percentage (%)</th>
                </tr>
              </thead>
              <tbody>
                {cycleData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.cycle}</td>
                    <td>
                      <input
                        type="number"
                        value={item.percentage}
                        onChange={(e) => {
                          const newData = [...cycleData];
                          newData[index].percentage = e.target.value;
                          setCycleData(newData);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="popup-actions">
              <button
                className="lt-btn"
                onClick={saveCycleChanges}
                disabled={savingCycle}
              >
                💾 {savingCycle ? "Saving..." : "Save Changes"}
              </button>

              <button
                className="lt-btn secondary"
                onClick={() => setShowCyclePopup(false)}
              >
                ✖ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LongtermTrackContainer;
